import React, { useState, useRef, useEffect } from "react";

// After a coach-proposed rewrite is resolved, the user's bubble shows
// the FINAL submitted text — but the educational "why" content
// disappears. This badge sits next to the bubble. Click to expand a
// popover with the original + the changes that were applied (same
// styling as the in-flight QueryRewriteCard so the teaching is
// preserved across the accept/edit/reject moment).

const TYPE_LABELS = {
  vocab:   "Vocabulary",
  scope:   "Scope",
  anchor:  "Anchor",
  resolve: "Disambiguation",
};

const TYPE_COLORS = {
  vocab:   { fg: "#9a3412", bg: "#fed7aa" },
  scope:   { fg: "#1e40af", bg: "#bfdbfe" },
  anchor:  { fg: "#166534", bg: "#bbf7d0" },
  resolve: { fg: "#6b21a8", bg: "#e9d5ff" },
};

const RESOLUTION_LABEL = {
  accepted: "Accepted",
  edited:   "Edited",
  rejected: "Used original",
};

const RewriteAppliedBadge = ({ rewriteApplied }) => {
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const ref = useRef(null);
  const popRef = useRef(null);
  const { original, changes = [], resolution } = rewriteApplied || {};

  const toggle = () => {
    if (open) {
      setOpen(false);
    } else if (ref.current) {
      setAnchorRect(ref.current.getBoundingClientRect());
      setOpen(true);
    }
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const inBadge = ref.current && ref.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inBadge && !inPop) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!rewriteApplied) return null;

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block", marginLeft: "6px" }}>
      <button
        type="button"
        onClick={toggle}
        title="See how coach reframed this query"
        style={{
          padding: "1px 8px",
          fontSize: "10px",
          fontWeight: 600,
          color: "#92400e",
          background: "#fef3c7",
          border: "1px solid #fcd34d",
          borderRadius: "10px",
          cursor: "pointer",
          fontFamily: "inherit",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        ✨ reframed{resolution === "edited" ? " (edited)" : resolution === "rejected" ? " (declined)" : ""}
      </button>

      {open && anchorRect && (
        <div ref={popRef} style={{
          position: "fixed",
          top: anchorRect.bottom + 6,
          right: Math.max(8, window.innerWidth - anchorRect.right),
          width: "min(420px, 80vw)",
          maxHeight: "60vh",
          overflowY: "auto",
          background: "#fffbeb",
          border: "1px solid #fcd34d",
          borderRadius: "8px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
          padding: "10px 12px",
          zIndex: 1000,
          fontSize: "12px",
          color: "#1f2937",
          textAlign: "left",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
            <span style={{ color: "#92400e", fontWeight: 600 }}>
              ✨ {RESOLUTION_LABEL[resolution] || "Reframed"}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                border: "none", background: "transparent",
                color: "#92400e", cursor: "pointer", fontSize: "14px", lineHeight: 1,
                padding: 0,
              }}
            >×</button>
          </div>

          <div style={{
            color: "#9a3412",
            fontSize: "10px",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            fontWeight: 600,
            marginBottom: "2px",
          }}>You typed</div>
          <div style={{
            color: "#78350f", fontSize: "12px", fontStyle: "italic",
            marginBottom: "10px", paddingLeft: "8px",
            borderLeft: "2px solid #fcd34d",
            whiteSpace: "pre-wrap", wordBreak: "break-word",
          }}>{original || "(empty)"}</div>

          {changes.length > 0 ? (
            <>
              <div style={{
                color: "#9a3412",
                fontSize: "10px",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: 600,
                marginBottom: "4px",
              }}>What changed</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                {changes.map((c, i) => {
                  const colors = TYPE_COLORS[c.type] || TYPE_COLORS.vocab;
                  return (
                    <div key={i} style={{
                      padding: "6px 8px",
                      background: "#fff",
                      border: "1px solid #fde68a",
                      borderLeft: `3px solid ${colors.fg}`,
                      borderRadius: "4px",
                      fontSize: "11px",
                    }}>
                      <div style={{ display: "flex", gap: "5px", alignItems: "baseline", marginBottom: "2px", flexWrap: "wrap" }}>
                        <span style={{
                          color: colors.fg,
                          background: colors.bg,
                          padding: "1px 5px",
                          borderRadius: "3px",
                          fontSize: "9px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}>{TYPE_LABELS[c.type] || c.type}</span>
                        <span style={{ color: "#6b7280" }}>
                          {c.before ? <s>{c.before}</s> : <em>(none)</em>}
                          {" → "}
                          <strong style={{ color: "#1f2937" }}>{c.after}</strong>
                        </span>
                      </div>
                      <div style={{ color: "#4b5563", lineHeight: "1.45" }}>{c.reason}</div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div style={{ color: "#6b7280", fontStyle: "italic" }}>
              (no individual changes were recorded)
            </div>
          )}
        </div>
      )}
    </span>
  );
};

export default RewriteAppliedBadge;
